import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useInformationStore } from "@/stores/informationStore";
import type { UpdateHeaderFormData } from "@/services/informationService";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { showToast } from "@/lib/toast";
import { Editor } from "@tinymce/tinymce-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Settings,
  Upload,
  Image as ImageIcon,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  X,
  AlertCircle,
  Monitor,
  Layout,
} from "lucide-react";

// Validation schema
const headerSchema = z.object({
  title: z.string().min(1, "Tiêu đề không được để trống"),
  title_seo: z.string().min(1, "Tiêu đề SEO không được để trống"),
  description_meta: z.string().min(1, "Mô tả meta không được để trống"),
  script: z.string().optional(),
});

type HeaderFormData = z.infer<typeof headerSchema>;

interface UploadedImageData {
  file: File;
  preview: string;
  alt?: string;
  title?: string;
  caption?: string;
  linkImage?: string;
  rel?: string;
  target?: string;
  width?: number;
  height?: number;
}

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (data: UploadedImageData) => void;
  type: "logo" | "favicon";
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  type,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [title, setTitle] = useState("");
  const [alt, setAlt] = useState("");
  const [caption, setCaption] = useState("");
  const [linkImage, setLinkImage] = useState("");
  const [rel, setRel] = useState<string>("nofollow");
  const [target, setTarget] = useState<string>("_self");
  const [imgWidth, setImgWidth] = useState<number | null>(null);
  const [imgHeight, setImgHeight] = useState<number | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const isValidUrl = (v: string) => {
    try {
      if (!v) return false;
      new URL(v);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setSelectedFile(null);
    setPreview("");
    setTitle("");
    setAlt("");
    setCaption("");
    setLinkImage("");
    setRel("nofollow");
    setTarget("_self");
    setImgWidth(null);
    setImgHeight(null);
  }, [isOpen]);

  const onFiles = (f: FileList | null) => {
    const fl = f?.[0];
    if (!fl) return;
    if (!fl.type.startsWith("image/")) {
      showToast.error("Lỗi", "Vui lòng chọn file hình ảnh");
      return;
    }
    setSelectedFile(fl);
    const url = URL.createObjectURL(fl);
    setPreview(url);

    // Tự động đặt tiêu đề từ tên file (bỏ extension)
    const nameWithoutExt = fl.name.replace(/\.[^/.]+$/, "");
    setTitle(nameWithoutExt);
    setAlt(nameWithoutExt);

    const img = new Image();
    img.onload = () => {
      setImgWidth(img.naturalWidth || (img as any).width || null);
      setImgHeight(img.naturalHeight || (img as any).height || null);
    };
    img.src = url;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    onSelect({
      file: selectedFile,
      preview,
      alt,
      title,
      caption,
      linkImage,
      rel,
      target,
      width: imgWidth || undefined,
      height: imgHeight || undefined,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-6xl p-0">
        <form onSubmit={handleSubmit}>
          <DialogHeader className="px-6 py-4 border-b border-gray-200">
            <DialogTitle>
              {type === "logo" ? "Chọn Logo" : "Chọn Favicon"}
            </DialogTitle>
          </DialogHeader>

          <div className="p-6">
            {!preview ? (
              <div className="flex justify-center items-center min-h-[400px]">
                <div
                  ref={dropRef}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={handleDrop}
                  className="w-full max-w-xl rounded-xl border-2 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-white py-20 px-8 text-center text-gray-600 hover:border-blue-400 hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className="flex flex-col items-center gap-4">
                    <div className="p-4 bg-blue-50 rounded-full">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-12 h-12 text-blue-500"
                      >
                        <path d="M2.25 6.75A2.25 2.25 0 014.5 4.5h15a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0119.5 19.5h-15A2.25 2.25 0 012.25 17.25V6.75zM6 9.75a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0z" />
                        <path d="M3 16.5l4.94-4.94a1.5 1.5 0 012.12 0L15 16.5l2.47-2.47a1.5 1.5 0 012.12 0L21 15.34V17.25A2.25 2.25 0 0118.75 19.5h-13.5A2.25 2.25 0 013 17.25v-.75z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-gray-700 mb-1">
                        Kéo & thả hoặc bấm để chọn ảnh
                      </div>
                      <div className="text-sm text-gray-500">
                        Hỗ trợ: JPG, PNG, GIF, WEBP (tối đa 10MB)
                      </div>
                    </div>
                    <label className="mt-2 inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-medium cursor-pointer hover:from-blue-700 hover:to-blue-600 transition-all shadow-md hover:shadow-lg">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                        />
                      </svg>
                      Chọn ảnh từ máy
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onFiles(e.target.files)}
                      />
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="relative bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex justify-center items-center">
                      <img
                        src={preview}
                        alt="Preview"
                        className="rounded-lg shadow-md max-h-80 w-auto object-contain"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <label className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white border-2 border-gray-300 text-gray-700 text-sm font-medium cursor-pointer hover:border-blue-500 hover:text-blue-600 transition-all">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                        />
                      </svg>
                      Đổi ảnh
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onFiles(e.target.files)}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setPreview("");
                        setSelectedFile(null);
                        setImgWidth(null);
                        setImgHeight(null);
                      }}
                      className="px-4 py-2 rounded-lg bg-red-50 border-2 border-red-200 text-red-600 text-sm font-medium hover:bg-red-100 hover:border-red-300 transition-all"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                        />
                      </svg>
                    </button>
                  </div>
                  {selectedFile && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                      <div className="text-xs font-semibold text-blue-700 mb-2 uppercase tracking-wide">
                        Thông tin tệp tin
                      </div>
                      <div className="space-y-2 text-sm text-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Tên tệp:</span>
                          <span
                            className="font-medium text-gray-900 truncate ml-2 max-w-[200px]"
                            title={selectedFile.name}
                          >
                            {selectedFile.name}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Loại:</span>
                          <span className="font-medium text-gray-900">
                            {selectedFile.type}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Dung lượng:</span>
                          <span className="font-medium text-gray-900">
                            {Math.round(selectedFile.size / 1024)} KB
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Kích thước:</span>
                          <span className="font-medium text-gray-900">
                            {imgWidth ?? "-"} x {imgHeight ?? "-"} px
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Thông tin ảnh
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Tiêu đề
                    </label>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Nhập tiêu đề cho ảnh..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Alt Text (SEO)
                    </label>
                    <input
                      value={alt}
                      onChange={(e) => setAlt(e.target.value)}
                      placeholder="Mô tả thay thế cho ảnh..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Caption
                    </label>
                    <input
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Chú thích cho ảnh..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Link ảnh{" "}
                      <span className="text-gray-500 font-normal">
                        (tùy chọn)
                      </span>
                    </label>
                    <input
                      value={linkImage}
                      onChange={(e) => setLinkImage(e.target.value)}
                      placeholder="https://example.com"
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        linkImage && !isValidUrl(linkImage)
                          ? "border-red-500 focus:ring-red-500 bg-red-50"
                          : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                      }`}
                    />
                    {linkImage && !isValidUrl(linkImage) && (
                      <p className="text-xs text-red-600 mt-1">
                        URL không hợp lệ
                      </p>
                    )}
                  </div>
                  {linkImage.trim() && isValidUrl(linkImage) && (
                    <div className="pt-3 border-t border-gray-200">
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                        Cấu hình liên kết
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                            Rel Attribute
                          </label>
                          <select
                            value={rel}
                            onChange={(e) => setRel(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                          >
                            <option value="nofollow">nofollow</option>
                            <option value="noreferrer">noreferrer</option>
                            <option value="noopener">noopener</option>
                            <option value="sponsored">sponsored</option>
                            <option value="ugc">ugc</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1.5">
                            Thuộc tính SEO cho liên kết
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                            Target
                          </label>
                          <select
                            value={target}
                            onChange={(e) => setTarget(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                          >
                            <option value="_self">_self (tab hiện tại)</option>
                            <option value="_blank">_blank (tab mới)</option>
                            <option value="_parent">_parent</option>
                            <option value="_top">_top</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1.5">
                            Cách mở liên kết
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium cursor-pointer"
            >
              Hủy
            </button>
            {preview && (
              <button
                type="submit"
                disabled={
                  !selectedFile ||
                  (!!linkImage.trim() && !isValidUrl(linkImage))
                }
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all font-medium shadow-md hover:shadow-lg"
              >
                Chọn ảnh
              </button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const InformationManagementPage: React.FC = () => {
  const { information, loading, error, fetchInformation, updateInformation } =
    useInformationStore();
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [isFaviconModalOpen, setIsFaviconModalOpen] = useState(false);
  const [showScript, setShowScript] = useState(true);

  // Local state cho file uploaded
  const [logoData, setLogoData] = useState<UploadedImageData | null>(null);
  const [faviconData, setFaviconData] = useState<UploadedImageData | null>(
    null
  );
  // Cờ xóa ảnh hiện có (không upload mới)
  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeFavicon, setRemoveFavicon] = useState(false);

  // Mở rộng metadata nâng cao
  const [showLogoAdvanced, setShowLogoAdvanced] = useState(false);
  const [showFaviconAdvanced, setShowFaviconAdvanced] = useState(false);

  // Metadata editable cho ảnh hiện có
  const [logoMeta, setLogoMeta] = useState({
    alt: "",
    title: "",
    caption: "",
    linkImage: "",
    rel: "nofollow",
    target: "_self",
  });
  const [faviconMeta, setFaviconMeta] = useState({
    alt: "",
    title: "",
    caption: "",
    linkImage: "",
    rel: "nofollow",
    target: "_self",
  });

  // Robots Meta state
  const [robotsMeta, setRobotsMeta] = useState({
    index: true,
    follow: true,
    noarchive: false,
    nosnippet: false,
    noimageindex: false,
    nocache: false,
    maxVideoPreview: -1 as number,
    maxImagePreview: "large" as "none" | "standard" | "large",
    maxSnippet: -1 as number,
  });

  // Verification Meta state
  const [verificationMeta, setVerificationMeta] = useState({
    google: "",
    bing: "",
    yandex: "",
    yahoo: "",
    ahrefs: "",
  });

  // Google Analytics ID state
  const [googleAnalyticsId, setGoogleAnalyticsId] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<HeaderFormData>({
    resolver: zodResolver(headerSchema),
    defaultValues: {
      title: "",
      title_seo: "",
      description_meta: "",
      script: "",
    },
  });

  // Load information data on mount
  useEffect(() => {
    fetchInformation();
  }, [fetchInformation]);

  // Reset form when information data loads
  useEffect(() => {
    if (information) {
      reset({
        title: information.title || "",
        title_seo: information.title_seo || "",
        description_meta: information.description_meta || "",
        script: information.script || "",
      });
      // Đồng bộ metadata hiện có để chỉnh sửa
      if (information.logo) {
        setLogoMeta({
          alt: information.logo.alt || "",
          title: information.logo.title || "",
          caption: information.logo.caption || "",
          linkImage: information.logo.linkImage || "",
          rel: (information.logo as any).rel || "nofollow",
          target: (information.logo as any).target || "_self",
        });
      }
      if (information.favicon) {
        setFaviconMeta({
          alt: information.favicon.alt || "",
          title: information.favicon.title || "",
          caption: information.favicon.caption || "",
          linkImage: information.favicon.linkImage || "",
          rel: (information.favicon as any).rel || "nofollow",
          target: (information.favicon as any).target || "_self",
        });
      }
      // Tự động hiện script khi có dữ liệu
      if (information.script) setShowScript(true);

      // Đồng bộ robots meta
      if (information.robots) {
        setRobotsMeta({
          index: information.robots.index ?? true,
          follow: information.robots.follow ?? true,
          noarchive: information.robots.noarchive ?? false,
          nosnippet: information.robots.nosnippet ?? false,
          noimageindex: information.robots.noimageindex ?? false,
          nocache: information.robots.nocache ?? false,
          maxVideoPreview:
            typeof information.robots["max-video-preview"] === "number"
              ? information.robots["max-video-preview"]
              : -1,
          maxImagePreview: information.robots["max-image-preview"] ?? "large",
          maxSnippet:
            typeof information.robots["max-snippet"] === "number"
              ? information.robots["max-snippet"]
              : -1,
        });
      }

      // Đồng bộ verification meta
      if (information.verification) {
        setVerificationMeta({
          google: information.verification.google ?? "",
          bing: information.verification.bing ?? "",
          yandex: information.verification.yandex ?? "",
          yahoo: information.verification.yahoo ?? "",
          ahrefs: information.verification.ahrefs ?? "",
        });
      }

      // Đồng bộ Google Analytics ID
      if (information.googleAnalyticsId) {
        setGoogleAnalyticsId(information.googleAnalyticsId);
      }
    }
  }, [information, reset]);

  const handleLogoSelect = (data: UploadedImageData) => {
    setLogoData(data);
    setRemoveLogo(false);
  };

  const handleFaviconSelect = (data: UploadedImageData) => {
    setFaviconData(data);
    setRemoveFavicon(false);
  };

  const handleRemoveLogo = () => {
    setLogoData(null);
    setRemoveLogo(true);
  };

  const handleRemoveFavicon = () => {
    setFaviconData(null);
    setRemoveFavicon(true);
  };

  const onSubmitHeader = async (data: HeaderFormData) => {
    try {
      const updateData: UpdateHeaderFormData = {
        title: data.title,
        title_seo: data.title_seo,
        description_meta: data.description_meta,
        script: data.script,
      };

      // Thêm logo nếu có file mới hoặc đang có logo hiện tại
      if (logoData) {
        updateData.logo = logoData.file;
        updateData.logoAlt = logoData.alt;
        updateData.logoTitle = logoData.title;
        updateData.logoCaption = logoData.caption;
        updateData.logoLinkImage = logoData.linkImage;
        updateData.logoRel = logoData.rel;
        updateData.logoTarget = logoData.target;
      } else if (removeLogo) {
        // Không gửi logoId và không gửi file => backend sẽ giữ nguyên; hiện chưa có API xóa.
        // Tối thiểu: đồng bộ UI và không gửi metadata để tránh overwrite ngoài ý muốn.
      } else if (information?.logo?._id) {
        // Giữ logo hiện tại
        updateData.logoId = information.logo._id;
        // Gửi metadata đã chỉnh sửa cho logo hiện tại
        updateData.logoAlt = logoMeta.alt;
        updateData.logoTitle = logoMeta.title;
        updateData.logoCaption = logoMeta.caption;
        updateData.logoLinkImage = logoMeta.linkImage;
        updateData.logoRel = logoMeta.rel;
        updateData.logoTarget = logoMeta.target;
      }

      // Thêm favicon nếu có file mới hoặc đang có favicon hiện tại
      if (faviconData) {
        updateData.favicon = faviconData.file;
        updateData.faviconAlt = faviconData.alt;
        updateData.faviconTitle = faviconData.title;
        updateData.faviconCaption = faviconData.caption;
        updateData.faviconLinkImage = faviconData.linkImage;
        updateData.faviconRel = faviconData.rel;
        updateData.faviconTarget = faviconData.target;
      } else if (removeFavicon) {
        // Như phần logo, hiện UI xóa cục bộ.
      } else if (information?.favicon?._id) {
        // Giữ favicon hiện tại
        updateData.faviconId = information.favicon._id;
        // Gửi metadata đã chỉnh sửa cho favicon hiện tại
        updateData.faviconAlt = faviconMeta.alt;
        updateData.faviconTitle = faviconMeta.title;
        updateData.faviconCaption = faviconMeta.caption;
        updateData.faviconLinkImage = faviconMeta.linkImage;
        updateData.faviconRel = faviconMeta.rel;
        updateData.faviconTarget = faviconMeta.target;
      }

      // Thêm robots meta
      updateData.robotsIndex = robotsMeta.index;
      updateData.robotsFollow = robotsMeta.follow;
      updateData.robotsNoarchive = robotsMeta.noarchive;
      updateData.robotsNosnippet = robotsMeta.nosnippet;
      updateData.robotsNoimageindex = robotsMeta.noimageindex;
      updateData.robotsNocache = robotsMeta.nocache;
      updateData.robotsMaxVideoPreview = robotsMeta.maxVideoPreview;
      updateData.robotsMaxImagePreview = robotsMeta.maxImagePreview;
      updateData.robotsMaxSnippet = robotsMeta.maxSnippet;

      // Thêm verification meta
      updateData.verificationGoogle = verificationMeta.google;
      updateData.verificationBing = verificationMeta.bing;
      updateData.verificationYandex = verificationMeta.yandex;
      updateData.verificationYahoo = verificationMeta.yahoo;
      updateData.verificationAhrefs = verificationMeta.ahrefs;

      // Thêm Google Analytics ID
      updateData.googleAnalyticsId = googleAnalyticsId;

      await updateInformation(updateData);
      showToast.success("Cập nhật thông tin Header thành công");

      // Clear uploaded data sau khi save thành công
      setLogoData(null);
      setFaviconData(null);
    } catch (error: any) {
      showToast.error(error.message || "Có lỗi xảy ra khi cập nhật Header");
    }
  };

  // Footer states
  const [footer, setFooter] = useState({
    column1: "",
    column2: "",
    column3: "",
    column4: "",
    column5: "",
  });

  // Fetch footer on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await useInformationStore.getState().fetchFooter();

        setFooter({
          column1: data?.data?.column1 || "",
          column2: data?.data?.column2 || "",
          column3: data?.data?.column3 || "",
          column4: data?.data?.column4 || "",
          column5: data?.data?.column5 || "",
        });
      } catch {}
    })();
  }, []);

  const onSubmitFooter = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await useInformationStore.getState().updateFooter(footer);
      showToast.success("Cập nhật thông tin Footer thành công");
    } catch (error: any) {
      showToast.error(error.message || "Có lỗi xảy ra khi cập nhật Footer");
    }
  };

  if (loading && !information) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // Hiển thị logo: ưu tiên logo mới upload, nếu không có thì hiển thị logo hiện tại
  const displayLogo = removeLogo ? null : logoData || information?.logo;
  const displayFavicon = removeFavicon
    ? null
    : faviconData || information?.favicon;

  return (
    <DashboardLayout>
      <div className=" mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
              <Monitor className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
                Quản lý thông tin
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <p className="text-gray-500 dark:text-gray-400">
                  Cấu hình thông tin header và footer cho website
                </p>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                  Cấu hình
                </span>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 border border-red-200 bg-red-50 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        <Tabs defaultValue="header" className="w-full">
          <TabsList className="mb-6 w-full">
            <TabsTrigger value="header" className="w-full hover:cursor-pointer">
              Header
            </TabsTrigger>
            <TabsTrigger value="footer" className="w-full hover:cursor-pointer">
              Footer
            </TabsTrigger>
          </TabsList>

          <TabsContent value="header">
            <form onSubmit={handleSubmit(onSubmitHeader)} className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Monitor className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    Header
                  </h2>
                  <p className="text-gray-500">
                    Cấu hình thông tin header của website
                  </p>
                </div>
              </div>

              {/* Basic Header Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Thông tin cơ bản
                  </CardTitle>
                  <CardDescription>
                    Cấu hình tiêu đề, SEO và mô tả cho website
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="title">Tiêu đề website</Label>
                    <Input
                      id="title"
                      {...register("title")}
                      placeholder="Nhập tiêu đề website"
                      className="mt-1"
                    />
                    {errors.title && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.title.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="title_seo">Tiêu đề SEO</Label>
                    <Input
                      id="title_seo"
                      {...register("title_seo")}
                      placeholder="Nhập tiêu đề SEO"
                      className="mt-1"
                    />
                    {errors.title_seo && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.title_seo.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="description_meta">Mô tả meta</Label>
                    <Textarea
                      id="description_meta"
                      {...register("description_meta")}
                      placeholder="Nhập mô tả meta cho SEO"
                      className="mt-1"
                      rows={3}
                    />
                    {errors.description_meta && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.description_meta.message}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Logo Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Logo website
                  </CardTitle>
                  <CardDescription>
                    Logo hiển thị trên header của website
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {displayLogo ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 p-4 border rounded-lg bg-gray-50">
                        <img
                          src={logoData?.preview || (displayLogo as any).url}
                          alt={(displayLogo as any).alt || "Logo"}
                          className="w-20 h-20 object-contain"
                        />
                        <div className="flex-1">
                          <p className="font-medium">
                            {(displayLogo as any).title}
                          </p>
                          <p className="text-sm text-gray-600">
                            {(displayLogo as any).alt}
                          </p>
                          {(displayLogo as any).width &&
                            (displayLogo as any).height && (
                              <Badge variant="secondary" className="mt-1">
                                {(displayLogo as any).width} x{" "}
                                {(displayLogo as any).height}
                              </Badge>
                            )}
                          {logoData && (
                            <Badge variant="outline" className="mt-1 ml-2">
                              Ảnh mới
                            </Badge>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveLogo}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Editable metadata (cơ bản + mở rộng) cho logo hiện có */}
                      {!logoData && information?.logo && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label>Tiêu đề</Label>
                              <Input
                                value={logoMeta.title}
                                onChange={(e) =>
                                  setLogoMeta({
                                    ...logoMeta,
                                    title: e.target.value,
                                  })
                                }
                                placeholder="Nhập tiêu đề ảnh"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>Alt Text</Label>
                              <Input
                                value={logoMeta.alt}
                                onChange={(e) =>
                                  setLogoMeta({
                                    ...logoMeta,
                                    alt: e.target.value,
                                  })
                                }
                                placeholder="Mô tả thay thế (SEO)"
                                className="mt-1"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <Label>Caption</Label>
                              <Input
                                value={logoMeta.caption}
                                onChange={(e) =>
                                  setLogoMeta({
                                    ...logoMeta,
                                    caption: e.target.value,
                                  })
                                }
                                placeholder="Chú thích ảnh"
                                className="mt-1"
                              />
                            </div>
                          </div>

                          <div className="pt-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setShowLogoAdvanced(!showLogoAdvanced)
                              }
                            >
                              {showLogoAdvanced ? "Thu gọn" : "Mở rộng"}
                            </Button>
                          </div>

                          {showLogoAdvanced && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="md:col-span-2">
                                <Label>Link ảnh</Label>
                                <Input
                                  value={logoMeta.linkImage}
                                  onChange={(e) =>
                                    setLogoMeta({
                                      ...logoMeta,
                                      linkImage: e.target.value,
                                    })
                                  }
                                  placeholder="https://example.com"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label>Rel</Label>
                                <select
                                  value={logoMeta.rel}
                                  onChange={(e) =>
                                    setLogoMeta({
                                      ...logoMeta,
                                      rel: e.target.value,
                                    })
                                  }
                                  className="mt-1 w-full border rounded-md h-10 px-3 bg-white"
                                >
                                  <option value="nofollow">nofollow</option>
                                  <option value="noreferrer">noreferrer</option>
                                  <option value="noopener">noopener</option>
                                  <option value="sponsored">sponsored</option>
                                  <option value="ugc">ugc</option>
                                </select>
                              </div>
                              <div>
                                <Label>Target</Label>
                                <select
                                  value={logoMeta.target}
                                  onChange={(e) =>
                                    setLogoMeta({
                                      ...logoMeta,
                                      target: e.target.value,
                                    })
                                  }
                                  className="mt-1 w-full border rounded-md h-10 px-3 bg-white"
                                >
                                  <option value="_self">_self</option>
                                  <option value="_blank">_blank</option>
                                  <option value="_parent">_parent</option>
                                  <option value="_top">_top</option>
                                </select>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsLogoModalOpen(true)}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Thay đổi logo
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                      <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600 mb-4">Chưa có logo</p>
                      <Button
                        type="button"
                        onClick={() => setIsLogoModalOpen(true)}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Chọn logo
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Favicon Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Favicon
                  </CardTitle>
                  <CardDescription>
                    Icon hiển thị trên tab trình duyệt
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {displayFavicon ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 p-4 border rounded-lg bg-gray-50">
                        <img
                          src={
                            faviconData?.preview || (displayFavicon as any).url
                          }
                          alt={(displayFavicon as any).alt || "Favicon"}
                          className="w-16 h-16 object-contain"
                        />
                        <div className="flex-1">
                          <p className="font-medium">
                            {(displayFavicon as any).title}
                          </p>
                          <p className="text-sm text-gray-600">
                            {(displayFavicon as any).alt}
                          </p>
                          {(displayFavicon as any).width &&
                            (displayFavicon as any).height && (
                              <Badge variant="secondary" className="mt-1">
                                {(displayFavicon as any).width} x{" "}
                                {(displayFavicon as any).height}
                              </Badge>
                            )}
                          {faviconData && (
                            <Badge variant="outline" className="mt-1 ml-2">
                              Ảnh mới
                            </Badge>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveFavicon}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      {!faviconData && information?.favicon && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label>Tiêu đề</Label>
                              <Input
                                value={faviconMeta.title}
                                onChange={(e) =>
                                  setFaviconMeta({
                                    ...faviconMeta,
                                    title: e.target.value,
                                  })
                                }
                                placeholder="Nhập tiêu đề ảnh"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>Alt Text</Label>
                              <Input
                                value={faviconMeta.alt}
                                onChange={(e) =>
                                  setFaviconMeta({
                                    ...faviconMeta,
                                    alt: e.target.value,
                                  })
                                }
                                placeholder="Mô tả thay thế (SEO)"
                                className="mt-1"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <Label>Caption</Label>
                              <Input
                                value={faviconMeta.caption}
                                onChange={(e) =>
                                  setFaviconMeta({
                                    ...faviconMeta,
                                    caption: e.target.value,
                                  })
                                }
                                placeholder="Chú thích ảnh"
                                className="mt-1"
                              />
                            </div>
                          </div>

                          <div className="pt-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setShowFaviconAdvanced(!showFaviconAdvanced)
                              }
                            >
                              {showFaviconAdvanced ? "Thu gọn" : "Mở rộng"}
                            </Button>
                          </div>

                          {showFaviconAdvanced && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="md:col-span-2">
                                <Label>Link ảnh</Label>
                                <Input
                                  value={faviconMeta.linkImage}
                                  onChange={(e) =>
                                    setFaviconMeta({
                                      ...faviconMeta,
                                      linkImage: e.target.value,
                                    })
                                  }
                                  placeholder="https://example.com"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label>Rel</Label>
                                <select
                                  value={faviconMeta.rel}
                                  onChange={(e) =>
                                    setFaviconMeta({
                                      ...faviconMeta,
                                      rel: e.target.value,
                                    })
                                  }
                                  className="mt-1 w-full border rounded-md h-10 px-3 bg-white"
                                >
                                  <option value="nofollow">nofollow</option>
                                  <option value="noreferrer">noreferrer</option>
                                  <option value="noopener">noopener</option>
                                  <option value="sponsored">sponsored</option>
                                  <option value="ugc">ugc</option>
                                </select>
                              </div>
                              <div>
                                <Label>Target</Label>
                                <select
                                  value={faviconMeta.target}
                                  onChange={(e) =>
                                    setFaviconMeta({
                                      ...faviconMeta,
                                      target: e.target.value,
                                    })
                                  }
                                  className="mt-1 w-full border rounded-md h-10 px-3 bg-white"
                                >
                                  <option value="_self">_self</option>
                                  <option value="_blank">_blank</option>
                                  <option value="_parent">_parent</option>
                                  <option value="_top">_top</option>
                                </select>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsFaviconModalOpen(true)}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Thay đổi favicon
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                      <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600 mb-4">Chưa có favicon</p>
                      <Button
                        type="button"
                        onClick={() => setIsFaviconModalOpen(true)}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Chọn favicon
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Script Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Script tùy chỉnh
                  </CardTitle>
                  <CardDescription>
                    Thêm script Google Analytics, Facebook Pixel, v.v.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowScript(!showScript)}
                      >
                        {showScript ? (
                          <>
                            <EyeOff className="w-4 h-4 mr-2" />
                            Ẩn script
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4 mr-2" />
                            Hiện script
                          </>
                        )}
                      </Button>
                      {information?.script && (
                        <Badge variant="secondary">
                          {information.script.length} ký tự
                        </Badge>
                      )}
                    </div>

                    {showScript && (
                      <Textarea
                        {...register("script")}
                        placeholder="<!-- Google Analytics -->&#10;<script>...</script>"
                        className="font-mono text-sm"
                        rows={8}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Robots Meta Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Robots Meta
                  </CardTitle>
                  <CardDescription>
                    Cấu hình chỉ thị cho các công cụ tìm kiếm (Google, Bing,
                    v.v.)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Toggle switches */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={robotsMeta.index}
                          onChange={(e) =>
                            setRobotsMeta({
                              ...robotsMeta,
                              index: e.target.checked,
                            })
                          }
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div>
                          <div className="font-medium text-sm">Index</div>
                          <div className="text-xs text-gray-500">
                            Cho phép lập chỉ mục
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={robotsMeta.follow}
                          onChange={(e) =>
                            setRobotsMeta({
                              ...robotsMeta,
                              follow: e.target.checked,
                            })
                          }
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div>
                          <div className="font-medium text-sm">Follow</div>
                          <div className="text-xs text-gray-500">
                            Theo dõi các liên kết
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={robotsMeta.noarchive}
                          onChange={(e) =>
                            setRobotsMeta({
                              ...robotsMeta,
                              noarchive: e.target.checked,
                            })
                          }
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div>
                          <div className="font-medium text-sm">Noarchive</div>
                          <div className="text-xs text-gray-500">
                            Không lưu bản cache
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={robotsMeta.nosnippet}
                          onChange={(e) =>
                            setRobotsMeta({
                              ...robotsMeta,
                              nosnippet: e.target.checked,
                            })
                          }
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div>
                          <div className="font-medium text-sm">Nosnippet</div>
                          <div className="text-xs text-gray-500">
                            Không hiện trích dẫn
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={robotsMeta.noimageindex}
                          onChange={(e) =>
                            setRobotsMeta({
                              ...robotsMeta,
                              noimageindex: e.target.checked,
                            })
                          }
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div>
                          <div className="font-medium text-sm">
                            Noimageindex
                          </div>
                          <div className="text-xs text-gray-500">
                            Không index hình ảnh
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={robotsMeta.nocache}
                          onChange={(e) =>
                            setRobotsMeta({
                              ...robotsMeta,
                              nocache: e.target.checked,
                            })
                          }
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div>
                          <div className="font-medium text-sm">Nocache</div>
                          <div className="text-xs text-gray-500">
                            Không cache trang
                          </div>
                        </div>
                      </label>
                    </div>

                    {/* Advanced options */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                      <div>
                        <Label htmlFor="maxImagePreview">
                          Max Image Preview
                        </Label>
                        <select
                          id="maxImagePreview"
                          value={robotsMeta.maxImagePreview}
                          onChange={(e) =>
                            setRobotsMeta({
                              ...robotsMeta,
                              maxImagePreview: e.target.value as
                                | "none"
                                | "standard"
                                | "large",
                            })
                          }
                          className="mt-1 w-full border rounded-md h-10 px-3 bg-white"
                        >
                          <option value="none">None</option>
                          <option value="standard">Standard</option>
                          <option value="large">Large</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Kích thước preview hình ảnh
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="maxVideoPreview">
                          Max Video Preview
                        </Label>
                        <Input
                          id="maxVideoPreview"
                          type="number"
                          value={robotsMeta.maxVideoPreview}
                          onChange={(e) =>
                            setRobotsMeta({
                              ...robotsMeta,
                              maxVideoPreview: parseInt(e.target.value) || -1,
                            })
                          }
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Giây preview video (-1 = unlimited)
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="maxSnippet">Max Snippet</Label>
                        <Input
                          id="maxSnippet"
                          type="number"
                          value={robotsMeta.maxSnippet}
                          onChange={(e) =>
                            setRobotsMeta({
                              ...robotsMeta,
                              maxSnippet: parseInt(e.target.value) || -1,
                            })
                          }
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Ký tự snippet (-1 = unlimited)
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Verification Meta Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Xác minh Search Engine
                  </CardTitle>
                  <CardDescription>
                    Mã xác minh từ các công cụ tìm kiếm (Google Search Console,
                    Bing Webmaster, v.v.)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="verificationGoogle">
                        Google Search Console
                      </Label>
                      <Input
                        id="verificationGoogle"
                        value={verificationMeta.google}
                        onChange={(e) =>
                          setVerificationMeta({
                            ...verificationMeta,
                            google: e.target.value,
                          })
                        }
                        placeholder="Nhập mã xác minh Google"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Mã từ Google Search Console
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="verificationBing">Bing Webmaster</Label>
                      <Input
                        id="verificationBing"
                        value={verificationMeta.bing}
                        onChange={(e) =>
                          setVerificationMeta({
                            ...verificationMeta,
                            bing: e.target.value,
                          })
                        }
                        placeholder="Nhập mã xác minh Bing"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Mã từ Bing Webmaster Tools
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="verificationYandex">
                        Yandex Webmaster
                      </Label>
                      <Input
                        id="verificationYandex"
                        value={verificationMeta.yandex}
                        onChange={(e) =>
                          setVerificationMeta({
                            ...verificationMeta,
                            yandex: e.target.value,
                          })
                        }
                        placeholder="Nhập mã xác minh Yandex"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Mã từ Yandex Webmaster
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="verificationYahoo">Yahoo</Label>
                      <Input
                        id="verificationYahoo"
                        value={verificationMeta.yahoo}
                        onChange={(e) =>
                          setVerificationMeta({
                            ...verificationMeta,
                            yahoo: e.target.value,
                          })
                        }
                        placeholder="Nhập mã xác minh Yahoo"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Mã từ Yahoo Site Explorer
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="verificationAhrefs">Ahrefs</Label>
                      <Input
                        id="verificationAhrefs"
                        value={verificationMeta.ahrefs}
                        onChange={(e) =>
                          setVerificationMeta({
                            ...verificationMeta,
                            ahrefs: e.target.value,
                          })
                        }
                        placeholder="Nhập mã xác minh Ahrefs"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Mã từ Ahrefs Webmaster Tools
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Google Analytics Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Google Analytics
                  </CardTitle>
                  <CardDescription>
                    Cấu hình Google Analytics để theo dõi lưu lượng truy cập
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label htmlFor="googleAnalyticsId">
                      Google Analytics ID
                    </Label>
                    <Input
                      id="googleAnalyticsId"
                      value={googleAnalyticsId}
                      onChange={(e) => setGoogleAnalyticsId(e.target.value)}
                      placeholder="G-XXXXXXXXXX"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      ID theo dõi Google Analytics 4 (bắt đầu bằng G-)
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Header Action Buttons */}
              <div className="flex justify-end gap-4 pt-4 border-t border-blue-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fetchInformation()}
                  disabled={loading}
                >
                  <RefreshCw
                    className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
                  />
                  Làm mới
                </Button>
                <Button type="submit" disabled={isSubmitting || loading}>
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Lưu Header
                    </>
                  )}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="footer">
            <form onSubmit={onSubmitFooter} className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Layout className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    Footer
                  </h2>
                  <p className="text-gray-500">
                    Cấu hình thông tin footer của website
                  </p>
                </div>
              </div>

              {/* Footer Content */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Nội dung Footer
                  </CardTitle>
                  <CardDescription>
                    5 cột nội dung hiển thị ở footer website (TinyMCE)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  <div className="space-y-6">
                    <div>
                      <Label className="mb-2 block">Cột 1</Label>
                      <Editor
                        tinymceScriptSrc="/tinymce/tinymce.min.js"
                        licenseKey="gpl"
                        value={footer.column1}
                        onEditorChange={(content) =>
                          setFooter((s) => ({ ...s, column1: content }))
                        }
                        init={{
                          height: 400,
                          menubar: true,
                          plugins: [
                            "code",
                            "advlist",
                            "anchor",
                            "autolink",
                            "charmap",
                            "fullscreen",
                            "help",
                            "image",
                            "insertdatetime",
                            "link",
                            "lists",
                            "media",
                            "preview",
                            "searchreplace",
                            "table",
                            "visualblocks",
                          ],
                          toolbar:
                            "code | undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image",
                          content_style:
                            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                        }}
                      />
                    </div>

                    <div>
                      <Label className="mb-2 block">Cột 2</Label>
                      <Editor
                        tinymceScriptSrc="/tinymce/tinymce.min.js"
                        licenseKey="gpl"
                        value={footer.column2}
                        onEditorChange={(content) =>
                          setFooter((s) => ({ ...s, column2: content }))
                        }
                        init={{
                          height: 400,
                          menubar: true,
                          plugins: [
                            "code",
                            "advlist",
                            "anchor",
                            "autolink",
                            "charmap",
                            "fullscreen",
                            "help",
                            "image",
                            "insertdatetime",
                            "link",
                            "lists",
                            "media",
                            "preview",
                            "searchreplace",
                            "table",
                            "visualblocks",
                          ],
                          toolbar:
                            "code | undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image",
                          content_style:
                            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                        }}
                      />
                    </div>

                    <div>
                      <Label className="mb-2 block">Cột 3</Label>
                      <Editor
                        tinymceScriptSrc="/tinymce/tinymce.min.js"
                        licenseKey="gpl"
                        value={footer.column3}
                        onEditorChange={(content) =>
                          setFooter((s) => ({ ...s, column3: content }))
                        }
                        init={{
                          height: 400,
                          menubar: true,
                          plugins: [
                            "code",
                            "advlist",
                            "anchor",
                            "autolink",
                            "charmap",
                            "fullscreen",
                            "help",
                            "image",
                            "insertdatetime",
                            "link",
                            "lists",
                            "media",
                            "preview",
                            "searchreplace",
                            "table",
                            "visualblocks",
                          ],
                          toolbar:
                            "code | undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image",
                          content_style:
                            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                        }}
                      />
                    </div>

                    <div>
                      <Label className="mb-2 block">Cột 4</Label>
                      <Editor
                        tinymceScriptSrc="/tinymce/tinymce.min.js"
                        licenseKey="gpl"
                        value={footer.column4}
                        onEditorChange={(content) =>
                          setFooter((s) => ({ ...s, column4: content }))
                        }
                        init={{
                          height: 400,
                          menubar: true,
                          plugins: [
                            "code",
                            "advlist",
                            "anchor",
                            "autolink",
                            "charmap",
                            "fullscreen",
                            "help",
                            "image",
                            "insertdatetime",
                            "link",
                            "lists",
                            "media",
                            "preview",
                            "searchreplace",
                            "table",
                            "visualblocks",
                          ],
                          toolbar:
                            "code | undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image",
                          content_style:
                            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                        }}
                      />
                    </div>

                    <div>
                      <Label className="mb-2 block">Cột 5</Label>
                      <Editor
                        tinymceScriptSrc="/tinymce/tinymce.min.js"
                        licenseKey="gpl"
                        value={footer.column5}
                        onEditorChange={(content) =>
                          setFooter((s) => ({ ...s, column5: content }))
                        }
                        init={{
                          height: 400,
                          menubar: true,
                          plugins: [
                            "code",
                            "advlist",
                            "anchor",
                            "autolink",
                            "charmap",
                            "fullscreen",
                            "help",
                            "image",
                            "insertdatetime",
                            "link",
                            "lists",
                            "media",
                            "preview",
                            "searchreplace",
                            "table",
                            "visualblocks",
                          ],
                          toolbar:
                            "code | undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image",
                          content_style:
                            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                        }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Footer Action Buttons */}
              <div className="flex justify-end gap-4 pt-4 border-t border-purple-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fetchInformation()}
                  disabled={loading}
                >
                  <RefreshCw
                    className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
                  />
                  Làm mới
                </Button>
                <Button type="submit" disabled={isSubmitting || loading}>
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Lưu Footer
                    </>
                  )}
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>

        {/* Modals */}
        <ImageUploadModal
          isOpen={isLogoModalOpen}
          onClose={() => setIsLogoModalOpen(false)}
          onSelect={handleLogoSelect}
          type="logo"
        />

        <ImageUploadModal
          isOpen={isFaviconModalOpen}
          onClose={() => setIsFaviconModalOpen(false)}
          onSelect={handleFaviconSelect}
          type="favicon"
        />
      </div>
    </DashboardLayout>
  );
};

export default InformationManagementPage;
